import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;



const TurbineDetail = () => {
  const { turbineId } = useParams();
  const [projects, setProjects] = useState([]);

  const turbineData = {
    francis: {
      name: 'Francis Turbine',
      fullDescription: 'The Francis turbine is a reaction turbine that operates in a mixed-flow regime, combining radial and axial flow characteristics. It is the most widely used turbine type for medium to high head hydropower applications due to its high efficiency across a wide range of operating conditions.',
      specs: {
        headRange: '10 - 350 meters',
        flowRange: 'Variable (Medium to High)',
        efficiency: '85 - 95%',
        speedRange: '75 - 1000 RPM',
        powerRange: '10 kW - 800 MW'
      },
      technicalDetails: [
        'Spiral casing ensures uniform water distribution',
        'Stay vanes provide structural support and guide flow',
        'Guide vanes control flow rate and turbine power output',
        'Runner blades designed for optimal energy extraction',
        'Draft tube recovers kinetic energy and maintains suction'
      ],
      applications: [
        'Run-of-river hydro plants',
        'Storage-based hydro plants',
        'Pumped storage schemes',
        'Medium head installations'
      ],
      cadModels: [
        {
          title: 'Complete Francis Turbine Assembly',
          description: '3D CAD model showing spiral casing, guide vanes, runner, and draft tube',
          image: 'https://images.unsplash.com/photo-1709131121922-028189a2c984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxoeWRybyUyMHBvd2VyJTIwcGxhbnQlMjBkYW0lMjBzY2VuaWN8ZW58MHx8fHwxNzcwMjkyNTY1fDA&ixlib=rb-4.1.0&q=85'
        },
        {
          title: 'Runner Blade Design',
          description: 'Detailed blade geometry and flow analysis',
          image: 'https://images.unsplash.com/photo-1759159091682-3b98f4759367?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwY29udHJvbCUyMHBhbmVsJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzAyOTI1ODN8MA&ixlib=rb-4.1.0&q=85'
        }
      ]
    },
    kaplan: {
      name: 'Kaplan Turbine',
      fullDescription: 'The Kaplan turbine is a propeller-type reaction turbine with adjustable blades, designed for low head and high flow applications. Its variable blade pitch mechanism allows it to maintain high efficiency across a wide range of flow conditions.',
      specs: {
        headRange: '2 - 40 meters',
        flowRange: 'High (50 - 700 m³/s)',
        efficiency: '80 - 90%',
        speedRange: '50 - 600 RPM',
        powerRange: '100 kW - 400 MW'
      },
      technicalDetails: [
        'Adjustable runner blades for part-load efficiency',
        'Double regulation system (guide vanes + runner blades)',
        'Vertical or horizontal shaft configurations',
        'Bulb, pit, or S-type installations',
        'Wicket gates for flow control'
      ],
      applications: [
        'Low head run-of-river plants',
        'Tidal power generation',
        'River barrage schemes',
        'Irrigation canal drops'
      ],
      cadModels: [
        {
          title: 'Kaplan Turbine Assembly',
          description: 'Complete assembly with blade adjustment mechanism',
          image: 'https://images.unsplash.com/photo-1769697148689-4ae92b0d8848?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxoeWRybyUyMHBvd2VyJTIwcGxhbnQlMjBkYW0lMjBzY2VuaWN8ZW58MHx8fHwxNzcwMjkyNTY1fDA&ixlib=rb-4.1.0&q=85'
        },
        {
          title: 'Blade Pitch Control System',
          description: 'Hydraulic actuation system for blade angle adjustment',
          image: 'https://images.unsplash.com/photo-1758873263914-230f8ff60569?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwY29udHJvbCUyMHBhbmVsJTIwYXV0b21hdGlvbnxlbnwwfHx8fDE3NzAyOTI1ODN8MA&ixlib=rb-4.1.0&q=85'
        }
      ]
    },
    pelton: {
      name: 'Pelton Turbine',
      fullDescription: 'The Pelton turbine is an impulse turbine designed for high head, low flow applications. Water jets strike the buckets mounted on the runner periphery, converting the kinetic energy of the jet into mechanical rotation.',
      specs: {
        headRange: '50 - 1300 meters',
        flowRange: 'Low (0.1 - 30 m³/s per jet)',
        efficiency: '85 - 92%',
        speedRange: '200 - 1000 RPM',
        powerRange: '1 MW - 400 MW'
      },
      technicalDetails: [
        'Multiple jets (typically 1-6) for power scaling',
        'Precision-machined bucket geometry',
        'Needle valve or spear for flow regulation',
        'Deflector plates for emergency shutdown',
        'Horizontal or vertical shaft configurations'
      ],
      applications: [
        'High head mountain streams',
        'Storage reservoir schemes',
        'Pumped storage (upper reservoir)',
        'Alpine hydropower plants'
      ],
      cadModels: [
        {
          title: 'Pelton Wheel Assembly',
          description: 'Multi-jet Pelton turbine with nozzle arrangement',
          image: 'https://images.unsplash.com/photo-1760343043912-80a4687c7cea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMHdhdGVyJTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDI5MjU4Nnww&ixlib=rb-4.1.0&q=85'
        },
        {
          title: 'Bucket Design & Jet Geometry',
          description: 'Optimized bucket shape for maximum energy extraction',
          image: 'https://images.unsplash.com/photo-1763507229005-c91a64090c48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYmx1ZSUyMHdhdGVyJTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDI5MjU4Nnww&ixlib=rb-4.1.0&q=85'
        }
      ]
    },
    crossflow: {
      name: 'Crossflow Turbine',
      fullDescription: 'The Crossflow turbine (also known as Banki-Michell turbine) is an impulse turbine with a drum-like runner. Water passes through the runner twice, entering from the periphery and exiting through the center, making it simple and cost-effective for small to medium hydro applications.',
      specs: {
        headRange: '2 - 200 meters',
        flowRange: 'Medium (0.05 - 10 m³/s)',
        efficiency: '75 - 85%',
        speedRange: '100 - 600 RPM',
        powerRange: '5 kW - 2 MW'
      },
      technicalDetails: [
        'Simple and robust construction',
        'Self-cleaning runner design',
        'Good part-load performance',
        'Minimal civil works required',
        'Easy maintenance and repair'
      ],
      applications: [
        'Small hydro projects',
        'Micro-hydro installations',
        'Rural electrification',
        'Irrigation canal systems'
      ],
      cadModels: [
        {
          title: 'Crossflow Turbine Assembly',
          description: 'Complete crossflow turbine with nozzle and runner',
          image: 'https://images.unsplash.com/photo-1763507229005-c91a64090c48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYmx1ZSUyMHdhdGVyJTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDI5MjU4Nnww&ixlib=rb-4.1.0&q=85'
        },
        {
          title: 'Runner Blade Configuration',
          description: 'Drum-type runner with curved blades',
          image: 'https://images.unsplash.com/photo-1761213230327-d89a8cc9cc63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxlbmdpbmVlciUyMGxvb2tpbmclMjBhdCUyMGJsdWVwcmludHMlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzcwMjkyNTg0fDA&ixlib=rb-4.1.0&q=85'
        }
      ]
    }
  };

  const turbine = turbineData[turbineId];

  useEffect(() => {
    if (turbine) {
      fetchProjects();
    }
  }, [turbineId]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const filteredProjects = response.data.filter(
        (project) => project.turbine_type && project.turbine_type.toLowerCase() === turbineId
      );
      setProjects(filteredProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  if (!turbine) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <h1 className="text-3xl text-white mb-4">Turbine not found</h1>
          <Link to="/design" className="text-ocean-500 hover:text-ocean-400">
            Back to Design
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid={`turbine-detail-${turbineId}`}>
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/design"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-ocean-500 transition-colors mb-8"
            data-testid="back-to-design"
          >
            <ArrowLeft size={20} />
            Back to Design
          </Link>
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-4xl md:text-6xl tracking-tighter text-white mb-6"
          >
            {turbine.name}
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-slate-400 max-w-4xl leading-relaxed"
          >
            {turbine.fullDescription}
          </motion.p>
        </div>
      </section>

      <section className="py-16 bg-slate-900" data-testid="specifications">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl font-medium text-white mb-8">Technical Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(turbine.specs).map(([key, value]) => (
              <div key={key} className="bg-slate-900/50 border border-white/5 p-6">
                <p className="text-slate-500 text-sm uppercase tracking-wider mb-2">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="font-mono text-ocean-400 text-lg">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-950" data-testid="cad-models">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl font-medium text-white mb-8">CAD Models & Design</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {turbine.cadModels.map((model, index) => (
              <div key={index} className="bg-slate-900 border border-white/5 overflow-hidden">
                <div className="h-64 overflow-hidden">
                  <img src={model.image} alt={model.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">{model.title}</h3>
                  <p className="text-slate-400 text-sm">{model.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900" data-testid="technical-details">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl font-medium text-white mb-6">Technical Features</h2>
              <ul className="space-y-3">
                {turbine.technicalDetails.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-400">
                    <span className="text-ocean-500 mt-1">•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-medium text-white mb-6">Applications</h2>
              <ul className="space-y-3">
                {turbine.applications.map((app, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-400">
                    <span className="text-ocean-500 mt-1">•</span>
                    {app}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {projects.length > 0 && (
        <section className="py-16 bg-slate-950" data-testid="related-projects">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-3xl font-medium text-white mb-8">
              Our {turbine.name} Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-slate-900 border border-white/5 overflow-hidden hover:border-ocean-500/30 transition-all"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-white mb-3">
                      {project.name}
                    </h3>
                    <div className="space-y-2 text-sm text-slate-400">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-ocean-500" />
                        {project.location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-ocean-500" />
                        {project.year}
                      </div>
                      <p className="font-mono text-ocean-400">{project.capacity}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TurbineDetail;
